import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { BadgeCheck, ChevronDown, KeyRound, LogOut, MailWarning, Trash2 } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { ReAuthModal } from './ReAuthModal';
import { Badge } from '../ui/Badge';
import { getAuthErrorMessage, needsReauth } from '../../lib/authErrors';

/** Header account dropdown: shows email + verification status and account actions. */
export function AccountMenu() {
  const navigate = useNavigate();
  const { user, emailVerified, signOut, deleteAccount } = useAuth();
  const [open, setOpen] = useState(false);
  const [reauthOpen, setReauthOpen] = useState(false);

  if (!user) return null;
  const initial = (user.email ?? '?').charAt(0).toUpperCase();

  const handleSignOut = async () => {
    await signOut();
    navigate('/login');
  };

  const runDelete = async () => {
    try {
      await deleteAccount();
      navigate('/signup');
    } catch (e) {
      if (needsReauth(e)) setReauthOpen(true);
      else alert(getAuthErrorMessage(e));
    }
  };

  return (
    <div className="relative">
      <button
        onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-2 pl-1.5 pr-2 py-1.5 rounded-full hover:bg-[#F5F5F5] transition-colors"
      >
        <span className="w-7 h-7 rounded-full bg-[#1A1A1A] text-white flex items-center justify-center text-xs font-bold">
          {initial}
        </span>
        <ChevronDown size={14} className="text-[#666666]" />
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
          <div className="absolute right-0 mt-2 w-64 bg-white rounded-2xl border border-[#E5E5E5] shadow-xl z-20 overflow-hidden">
            <div className="p-4 border-b border-[#F0F0F0] space-y-2">
              <p className="text-sm font-semibold truncate">{user.email}</p>
              {emailVerified ? (
                <Badge tone="success">
                  <BadgeCheck size={11} /> Verified
                </Badge>
              ) : (
                <Link to="/verify-email" onClick={() => setOpen(false)}>
                  <Badge tone="warning">
                    <MailWarning size={11} /> Unverified
                  </Badge>
                </Link>
              )}
            </div>
            <div className="p-2">
              <Link
                to="/payment/security/password"
                onClick={() => setOpen(false)}
                className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium text-[#444] hover:bg-[#F5F5F5]"
              >
                <KeyRound size={16} /> Change password
              </Link>
              <button
                onClick={handleSignOut}
                className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium text-[#444] hover:bg-[#F5F5F5]"
              >
                <LogOut size={16} /> Sign out
              </button>
              <button
                onClick={() => {
                  setOpen(false);
                  if (confirm('Delete your account permanently? This cannot be undone.')) void runDelete();
                }}
                className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium text-red-600 hover:bg-red-50"
              >
                <Trash2 size={16} /> Delete account
              </button>
            </div>
          </div>
        </>
      )}

      <ReAuthModal
        open={reauthOpen}
        onClose={() => setReauthOpen(false)}
        onSuccess={() => {
          setReauthOpen(false);
          void runDelete();
        }}
        title="Confirm account deletion"
        description="Please re-enter your password to permanently delete your account."
      />
    </div>
  );
}
